/* eslint-disable no-useless-escape */
import { ApiProductivityResponse } from '../types/api';
import { TableData, TableColumn } from '../components/GenericTable';
import { ChartDataset } from '../components/GenericBarChart';
import { CurveDataset } from '../components/GenericCurveChart';

export const PRODUCTIVITY_VALUE_INDICES = {
  QUANTITY: 0,           // Cantidad
  HH: 1,                 // HH
  PERIOD_PRODUCTIVITY: 2, // Productividad por Periodo *
  ACCUMULATED_QUANTITY: 3, // Cantidad Acumulada
  ACCUMULATED_HH: 4,      // HH Acumulada
  ACCUMULATED_PRODUCTIVITY: 5, // Productividad Acumulada *
} as const;

export const PRODUCTIVITY_LABELS = {
  PERIOD: 'Periodo',
  QUANTITY: 'Cantidad',
  HH: 'HH',
  PERIOD_PRODUCTIVITY: 'Productividad Periodo',
  ACCUMULATED_QUANTITY: 'Cantidad Acumulada',
  ACCUMULATED_HH: 'HH Acumulada',
  ACCUMULATED_PRODUCTIVITY: 'Productividad Acumulada',
  BUDGETED_PRODUCTIVITY: 'Productividad Presupuestada',
  WAITED_PRODUCTIVITY: 'Productividad Esperada',
} as const;

const extractProductivityValues = (valueString: string) => {
  const values = valueString.split('|');
  
  return {
    quantity: parseFloat(values[PRODUCTIVITY_VALUE_INDICES.QUANTITY]) || 0,
    hh: parseFloat(values[PRODUCTIVITY_VALUE_INDICES.HH]) || 0,
    periodProductivity: parseFloat(values[PRODUCTIVITY_VALUE_INDICES.PERIOD_PRODUCTIVITY]) || 0,
    accumulatedQuantity: parseFloat(values[PRODUCTIVITY_VALUE_INDICES.ACCUMULATED_QUANTITY]) || 0,
    accumulatedHH: parseFloat(values[PRODUCTIVITY_VALUE_INDICES.ACCUMULATED_HH]) || 0,
    accumulatedProductivity: parseFloat(values[PRODUCTIVITY_VALUE_INDICES.ACCUMULATED_PRODUCTIVITY]) || 0,
  };
};

export const transformProductivityTableData = (
  data: ApiProductivityResponse,
  period: 'week' | 'day' = 'week'
): TableData[] => {
  if (!data) return [];

  const cols = data.Cols.replace(/[\[\]]/g, '').split(',');
  
  const tableData: TableData[] = cols.map(col => {
    const value = data[col] as string;
    const values = extractProductivityValues(value);
    
    return {
      id: col,
      key: col,
      [PRODUCTIVITY_LABELS.PERIOD]: col,
      [PRODUCTIVITY_LABELS.PERIOD_PRODUCTIVITY]: values.periodProductivity.toFixed(4),
      [PRODUCTIVITY_LABELS.ACCUMULATED_PRODUCTIVITY]: values.accumulatedProductivity.toFixed(4),
      [PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY]: data.Productividad,
      [PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY]: data.ProductividadEsperada,
      _metadata: {
        periodType: period
      }
    };
  });
  return tableData;
};

export const transformProductivityChartData = (
  data: ApiProductivityResponse,
  period: 'week' | 'day' = 'week',
  options: {
    showBudgetedProductivity?: boolean;
    showOtherData?: boolean;
  } = {}
): {
  labels: string[];
  datasets: ChartDataset[];
  maxValue: number;
  shouldUseHorizontalLabels: boolean;
} => {
  const {
    showOtherData = false
  } = options;

  if (!data) {
    return {
      labels: [],
      datasets: [],
      maxValue: 100,
      shouldUseHorizontalLabels: true
    };
  }

  const cols = data.Cols.replace(/[\[\]]/g, '').split(',');
  const labels = cols.map(col => col);

  const periodProductivityData = cols.map(col => {
    const value = data[col] as string;
    const values = extractProductivityValues(value);
    return values.periodProductivity;
  });

  const accumulatedProductivityData = cols.map(col => {
    const value = data[col] as string;
    const values = extractProductivityValues(value);
    return values.accumulatedProductivity;
  });

  const budgetedProductivity = parseFloat(data.Productividad) || 0;
  const budgetedProductivityData = cols.map(() => budgetedProductivity);
  const waitedProductivity = parseFloat(data.ProductividadEsperada) || 0;
  const waitedProductivityData = cols.map(() => waitedProductivity);

  const quantityData = cols.map(col => {
    const value = data[col] as string;
    const values = extractProductivityValues(value);
    return values.quantity;
  });

  const hhData = cols.map(col => {
    const value = data[col] as string;
    const values = extractProductivityValues(value);
    return values.hh;
  });

  const accumulatedQuantityData = cols.map(col => {
    const value = data[col] as string;
    const values = extractProductivityValues(value);
    return values.accumulatedQuantity;
  });

  const accumulatedHHData = cols.map(col => {
    const value = data[col] as string;
    const values = extractProductivityValues(value);
    return values.accumulatedHH;
  });

  const allValues = [
    ...periodProductivityData, 
    ...accumulatedProductivityData, 
    budgetedProductivity,
    waitedProductivity,
    ...(showOtherData ? [...quantityData, ...hhData, ...accumulatedQuantityData, ...accumulatedHHData] : [])
  ];
  const maxValue = Math.max(...allValues) * 1.1;

  const itemCount = labels.length;
  const shouldUseHorizontalLabels = itemCount <= 12;

  const datasets: ChartDataset[] = [];

  datasets.push({
    label: PRODUCTIVITY_LABELS.PERIOD_PRODUCTIVITY,
    data: periodProductivityData,
    backgroundColor: 'rgba(54, 162, 235, 0.8)',
    borderColor: 'rgba(54, 162, 235, 1)',
    borderWidth: 1,
    type: 'bar',
  });

  datasets.push({
    label: PRODUCTIVITY_LABELS.ACCUMULATED_PRODUCTIVITY,
    data: accumulatedProductivityData,
    borderColor: 'rgba(75, 192, 192, 1)',
    backgroundColor: 'rgba(75, 192, 192, 0.2)',
    type: 'bar',
    tension: 0.4,
    fill: false
  });

  datasets.push({
    label: PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY,
    data: budgetedProductivityData,
    borderColor: 'rgba(255, 99, 132, 1)',
    backgroundColor: 'rgba(255, 99, 132, 0.1)',
    borderWidth: 2,
    borderDash: [5, 5],
    type: 'line',
    pointRadius: 0,
    fill: false
  });

  datasets.push({
    label: PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY,
    data: waitedProductivityData,
    borderColor: 'rgba(153, 102, 255, 1)',
    backgroundColor: 'rgba(153, 102, 255, 0.1)',
    borderWidth: 2,
    borderDash: [5, 5],
    type: 'line',
    pointRadius: 0,
    fill: false
  });

  if (showOtherData) {
    datasets.push(
      {
        label: PRODUCTIVITY_LABELS.QUANTITY,
        data: quantityData,
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        type: 'bar'
      },
      {
        label: PRODUCTIVITY_LABELS.HH,
        data: hhData,
        borderColor: 'rgba(255, 159, 64, 1)',
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        type: 'bar'
      },
      {
        label: PRODUCTIVITY_LABELS.ACCUMULATED_QUANTITY,
        data: accumulatedQuantityData,
        borderColor: 'rgba(201, 203, 207, 1)',
        backgroundColor: 'rgba(201, 203, 207, 0.2)',
        type: 'line'
      },
      {
        label: PRODUCTIVITY_LABELS.ACCUMULATED_HH,
        data: accumulatedHHData,
        borderColor: 'rgba(255, 205, 86, 1)',
        backgroundColor: 'rgba(255, 205, 86, 0.2)',
        type: 'line'
      }
    );
  }

  return {
    labels,
    datasets,
    maxValue,
    shouldUseHorizontalLabels
  };
};

export const transformProductivityCurveData = (
  data: ApiProductivityResponse,
  period: 'week' | 'day' = 'week'
): {
  labels: string[];
  datasets: CurveDataset[];
  maxValue: number;
  shouldUseHorizontalLabels: boolean;
} => {
  const chartData = transformProductivityChartData(data, period, {
    showOtherData: false
  });

  const curveDatasets: CurveDataset[] = chartData.datasets.map(dataset => ({
    label: dataset.label,
    data: dataset.data,
    borderColor: dataset.borderColor || 'rgba(75, 192, 192, 1)',
    backgroundColor: dataset.backgroundColor || 'rgba(75, 192, 192, 0.2)',
    borderWidth: dataset.borderWidth || 2,
    tension: 0.4,
    pointRadius: dataset.label === PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY || 
                 dataset.label === PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY ? 0 : 4,
    pointHoverRadius: dataset.label === PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY || 
                      dataset.label === PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY ? 0 : 6,
    fill: false,
    borderDash: dataset.label === PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY || 
                dataset.label === PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY ? [5, 5] : undefined,
  }));

  return {
    labels: chartData.labels,
    datasets: curveDatasets,
    maxValue: chartData.maxValue,
    shouldUseHorizontalLabels: chartData.shouldUseHorizontalLabels
  };
};

export const productivityTableColumns: TableColumn[] = [
  { 
    key: PRODUCTIVITY_LABELS.PERIOD, 
    label: PRODUCTIVITY_LABELS.PERIOD, 
    width: '20%' 
  },
  { 
    key: PRODUCTIVITY_LABELS.PERIOD_PRODUCTIVITY, 
    label: PRODUCTIVITY_LABELS.PERIOD_PRODUCTIVITY, 
    align: 'right', 
    width: '20%' 
  },
  { 
    key: PRODUCTIVITY_LABELS.ACCUMULATED_PRODUCTIVITY, 
    label: PRODUCTIVITY_LABELS.ACCUMULATED_PRODUCTIVITY, 
    align: 'right', 
    width: '20%' 
  },
  { 
    key: PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY, 
    label: PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY, 
    align: 'right', 
    width: '20%' 
  },
  { 
    key: PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY, 
    label: PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY, 
    align: 'right', 
    width: '20%' 
  },
];

export const productivityExtendedTableColumns: TableColumn[] = [
  { key: PRODUCTIVITY_LABELS.PERIOD, label: PRODUCTIVITY_LABELS.PERIOD, width: '15%' },
  { key: PRODUCTIVITY_LABELS.QUANTITY, label: PRODUCTIVITY_LABELS.QUANTITY, align: 'right', width: '12%' },
  { key: PRODUCTIVITY_LABELS.HH, label: PRODUCTIVITY_LABELS.HH, align: 'right', width: '12%' },
  { key: PRODUCTIVITY_LABELS.PERIOD_PRODUCTIVITY, label: PRODUCTIVITY_LABELS.PERIOD_PRODUCTIVITY, align: 'right', width: '12%' },
  { key: PRODUCTIVITY_LABELS.ACCUMULATED_QUANTITY, label: PRODUCTIVITY_LABELS.ACCUMULATED_QUANTITY, align: 'right', width: '12%' },
  { key: PRODUCTIVITY_LABELS.ACCUMULATED_HH, label: PRODUCTIVITY_LABELS.ACCUMULATED_HH, align: 'right', width: '12%' },
  { key: PRODUCTIVITY_LABELS.ACCUMULATED_PRODUCTIVITY, label: PRODUCTIVITY_LABELS.ACCUMULATED_PRODUCTIVITY, align: 'right', width: '12%' },
  { key: PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY, label: PRODUCTIVITY_LABELS.BUDGETED_PRODUCTIVITY, align: 'right', width: '13%' },
  { key: PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY, label: PRODUCTIVITY_LABELS.WAITED_PRODUCTIVITY, align: 'right', width: '13%' },
];

export const getProductivityRowBackgroundColor = (row: TableData) => {
  return '#ffffff';
};

export const getProductivityRowTextColor = (row: TableData) => {
  return '#000000';
};

export const getProductivityCellBackgroundColor = (row: TableData, columnKey: string) => {
  return getProductivityRowBackgroundColor(row);
};

export const getProductivityCellTextColor = (row: TableData, columnKey: string) => {
  return getProductivityRowTextColor(row);
};